import { useRef, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { Send, ShieldCheck, ShieldAlert, ShieldX, FileWarning, Loader2 } from "lucide-react";
import { DroneClassSelector } from "@/components/DroneClassSelector";
import { SearchBar } from "@/components/SearchBar";

const STATUS_CONFIG = {
  SAFE: { label: "SAFE TO FLY", color: "#2ec4b6", Icon: ShieldCheck, note: "Open G airspace · limit 400ft (120m)" },
  CAUTION: { label: "CAUTION / HIGH HAZARD", color: "#ffaa00", Icon: ShieldAlert, note: "HIRTA or military activity in this area" },
  PERMIT_REQUIRED: { label: "PERMIT / LOCAL BYLAW", color: "#3399ff", Icon: FileWarning, note: "Local council or SSSI restrictions apply" },
  STRICT_NO_FLY: { label: "STRICT NO FLY", color: "#ff4d4d", Icon: ShieldX, note: "Inside an active Flight Restriction Zone" },
};

const mdToHtml = (text) => {
  let html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^#{1,4}\s*(.+)$/gm, "<h3>$1</h3>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^\s*[-*]\s+(.+)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>[\s\S]*?<\/li>)(?!\s*<li>)/g, "<ul>$1</ul>");
  html = html.split(/\n{2,}/).map((p) => (p.startsWith("<") ? p : `<p>${p.replace(/\n/g, "<br/>")}</p>`)).join("");
  return DOMPurify.sanitize(html);
};

const ClearanceBanner = ({ status, zones }) => {
  if (!status) return null;
  const cfg = STATUS_CONFIG[status];
  const { Icon } = cfg;
  return (
    <div
      data-testid="clearance-banner"
      className="fade-up rounded-2xl px-4 py-3 border flex items-start gap-3"
      style={{ background: `${cfg.color}14`, borderColor: `${cfg.color}55` }}
    >
      <Icon size={22} style={{ color: cfg.color }} className="shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="font-heading font-bold text-base tracking-wide" style={{ color: cfg.color }} data-testid="clearance-status-text">
          {cfg.label}
        </p>
        <p className="text-sm text-zinc-200 mt-0.5">{cfg.note}</p>
        {zones?.length > 0 && (
          <p className="text-xs text-zinc-300 mt-1 truncate">{zones.map((z) => z.name).join(" · ")}</p>
        )}
      </div>
    </div>
  );
};

const AssistantMessage = ({ content }) => {
  const html = useMemo(() => DOMPurify.sanitize(mdToHtml(content)), [content]);
  return <div className="text-sm text-zinc-100 leading-relaxed chat-md" dangerouslySetInnerHTML={{ __html: html }} />;
};

const TypingIndicator = () => (
  <div className="flex gap-1.5 pl-1">
    {[0, 1, 2].map((i) => (
      <span key={i} className="typing-dot w-1.5 h-1.5 rounded-full bg-[#2ec4b6]" style={{ animationDelay: `${i * 0.2}s` }} />
    ))}
  </div>
);

export const MessageList = ({ messages, streaming, emptyTitle = "Maverick AI", emptyHint = "Your UK drone copilot. Tap anywhere on the map for an instant clearance report, or ask a question about UK drone law." }) => {
  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4" data-testid="chat-messages">
      {messages.length === 0 && (
        <div className="text-center pt-8 px-4">
          <p className="font-heading text-lg font-semibold text-zinc-100">{emptyTitle}</p>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{emptyHint}</p>
        </div>
      )}
      {messages.map((m) => (
        <div key={m.id} className={`fade-up ${m.role === "user" ? "flex justify-end" : ""}`}>
          {m.role === "user" ? (
            <div className="bg-[#2ec4b6]/15 border border-[#2ec4b6]/25 text-zinc-100 text-sm rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[85%]">
              {m.content}
            </div>
          ) : (
            <AssistantMessage content={m.content} />
          )}
        </div>
      ))}
      {streaming && messages[messages.length - 1]?.role === "user" && <TypingIndicator />}
    </div>
  );
};

const CHIPS = ["What are the rules here?", "Do I need a Flyer ID?", "Max altitude allowed?"];

export const AIPanel = ({
  weightClass, setWeightClass, assessment, messages, streaming, input, setInput, onSend, onSearchSelect, onLocate, locating,
}) => {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 sm:p-5 space-y-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="flex-1"><SearchBar onSelect={onSearchSelect} /></div>
          <button
            data-testid="locate-me-btn"
            onClick={onLocate}
            title="Use my location"
            className="shrink-0 w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-[#2ec4b6] hover:border-[#2ec4b6]/40 transition-colors"
          >
            {locating ? <Loader2 size={16} className="animate-spin" /> : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/><circle cx="12" cy="12" r="8"/></svg>
            )}
          </button>
        </div>
        <DroneClassSelector value={weightClass} onChange={setWeightClass} />
        <ClearanceBanner status={assessment?.status} zones={assessment?.zones} />
      </div>

      <MessageList messages={messages} streaming={streaming} />

      <div className="p-4 sm:p-5 border-t border-white/10 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <button
                key={c}
                data-testid="intent-chip"
                onClick={() => onSend(c)}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-zinc-200 hover:border-[#2ec4b6]/50 hover:text-[#2ec4b6] transition-colors"
              >
                {c}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !streaming && input.trim() && onSend(input)}
            placeholder="Ask Maverick…"
            aria-label="Chat message"
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none placeholder:text-zinc-400 text-zinc-100 focus:border-[#2ec4b6]/50 transition-colors"
          />
          <button
            data-testid="chat-send-btn"
            disabled={streaming || !input.trim()}
            onClick={() => onSend(input)}
            className="shrink-0 w-10 h-10 rounded-full bg-[#2ec4b6] text-black flex items-center justify-center disabled:opacity-30 hover:bg-[#3dd8ca] transition-colors"
          >
            {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
          </button>
        </div>
      </div>
    </div>
  );
};
